import React, { useRef } from "react";
import "../../styles/components/landing-pages/changelog.scss";
import Navbar from "./Navbar";
import Footer from "./Footer";

interface ChangeItem {
  type: "feature" | "improvement" | "fix";
  text: string;
}
interface ChangelogEntry {
  version: string;
  date: string;
  title: string;
  description: string;
  changes: ChangeItem[];
}

// ---------------------------------------------------------------------------
// 1. DATA
const entries: ChangelogEntry[] = [
  {
    version: "1.30",
    date: "2024-05-16",
    title: "Enhanced Resume Tailoring & New Cover Letter Features",
    description: "Introducing improved resume tailoring with better keyword matching and a completely redesigned cover‑letter experience.",
    changes: [
      { type: "feature", text: "Added AI‑powered keyword optimization for resumes" },
      { type: "feature", text: "New cover‑letter templates with industry‑specific variations" },
      { type: "improvement", text: "Improved job‑matching algorithm with 25% better accuracy" },
      { type: "improvement", text: "Enhanced user dashboard with clearer analytics" },
      { type: "fix", text: "Fixed PDF exports on Safari" },
      { type: "fix", text: "Resolved sporadic authentication errors" },
    ],
  },
  {
    version: "1.20",
    date: "2024-04-11",
    title: "Job‑Search Expansion & Performance Updates",
    description: "We've expanded job‑search coverage internationally and tuned performance throughout the app.",
    changes: [
      { type: "feature", text: "Support for international job boards" },
      { type: "feature", text: "Advanced filtering in search results" },
      { type: "improvement", text: "Reduced page‑load times by 40%" },
      { type: "improvement", text: "Sharper mobile responsiveness" },
    ],
  },
];

// ---------------------------------------------------------------------------
// 2. HELPERS
const formatDate = (d: string) =>
  new Date(d).toLocaleDateString("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
const formatVersion = (v: string) => (v.includes(".")) ? v : `${v[0]}.${v.slice(1)}`;

// ---------------------------------------------------------------------------
// 3. COMPONENT
const Changelog: React.FC = () => {
  const refs = useRef<(HTMLElement | null)[]>([]);

  return (
    <>
      <div className="changelog pg">
        <Navbar />

        {/* header */}
        <header className="hero">
          <h1>Product Changelog</h1>
          <div className="header-links">
            <a 
              href="https://x.com/QuickCruit" 
              target="_blank" 
              rel="noopener noreferrer"
              className="link-item"
            >
              Follow us on <svg className="x-logo" viewBox="0 0 24 24" aria-hidden="true"><g><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"></path></g></svg>
            </a>
          </div>
        </header>

        {/* entries */}
        <section className="timeline">
          {entries.map((e, i) => (
            <article
              key={e.version}
              ref={(el) => (refs.current[i] = el)}
              className="card"
            >
              <div className="card-meta">
                <span className="ver-tag">{formatVersion(e.version)}</span>
                <time>{formatDate(e.date)}</time>
              </div>
              <div className="card-body">
                <h2>{e.title}</h2>
                <p className="desc">{e.description}</p>
                <ul className="change-list">
                  {e.changes.map((c, idx) => (
                    <li key={idx}>
                      {c.text}
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Changelog;